/**
 * Text Rendering Utilities
 *
 * Functions for transforming plain text into decorated HTML with:
 * - Highlighted search terms
 * - Clickable links
 * - @mention chips
 *
 * These functions handle escaping, rendering, and combining various
 * text decorations while preserving safety and avoiding XSS.
 */

const DEFAULT_ANCHOR_CLASS = "underline cursor-pointer";
// Shared CSS classes for mention chips
export const MENTION_CHIP_CLASS =
  "inline-block bg-sky-100 text-sky-700 px-1.5 py-0.5 mt-[2px] rounded font-medium cursor-default select-none whitespace-nowrap";

/**
 * Escape HTML special characters to prevent XSS
 * @param {string} text - Text to escape
 * @returns {string} HTML-safe escaped text
 */
function escapeHtml(text) {
  const htmlEscapes = {
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  };
  return text.replace(/[&<>"']/g, (char) => htmlEscapes[char]);
}

/**
 * Highlight text matches with <mark> tags
 * @param {string} text - Plain text to highlight
 * @param {string} toReplaceRegex - Regex pattern to highlight
 * @returns {string} HTML with highlighted matches
 */
export function highlightText(text, toReplaceRegex) {
  try {
    // Escape HTML first to prevent XSS
    const escapedText = escapeHtml(text);
    const re = new RegExp(toReplaceRegex, "gi");

    // Apply highlighting on escaped text, then convert newlines
    return escapedText
      .replace(re, (match) => `<mark class='bg-amber-200'>${match}</mark>`)
      .replace(/\n/g, "<br>");
  } catch {
    // On error, still return escaped text with newlines converted
    return escapeHtml(text).replace(/\n/g, "<br>");
  }
}

/**
 * Convert bare URLs in text into clickable links that open in a new tab.
 * - Supports http(s) and www.* URLs
 * - Keeps query params intact and handles long/gnarly URLs
 * - Escapes original text to avoid introducing HTML
 * - Preserves simple trailing punctuation outside the anchor
 *
 * @param {string} text - Plain text potentially containing URLs
 * @param {string} anchorClass - CSS classes for the anchor tag
 * @returns {string} HTML with clickable links
 */
export function linkifyText(text, anchorClass = DEFAULT_ANCHOR_CLASS) {
  if (!text) return "";

  // Trick to safely escape arbitrary text to HTML entities
  const esc = (str) => {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  };

  let escaped = esc(text);

  const urlPattern = /(https?:\/\/[^\s<]+|www\.[^\s<]+)/gi;

  const result = escaped.replace(urlPattern, (matched) => {
    let working = matched;
    let trailing = "";

    // Remove simple trailing punctuation that's likely not part of the URL
    while (/[.,!?;:]$/.test(working)) {
      trailing = working.slice(-1) + trailing;
      working = working.slice(0, -1);
    }

    // Normalize bare www.* to absolute URLs
    let href = working.startsWith("www.") ? `http://${working}` : working;

    try {
      const url = new URL(href);
      if (url.protocol !== "http:" && url.protocol !== "https:") {
        return matched + trailing;
      }

      // Avoid breaking attributes with unexpected quotes (rare in URLs)
      const hrefAttr = url.toString().replace(/"/g, "&quot;");
      const classAttr =
        anchorClass && anchorClass.trim().length > 0
          ? ` class="${anchorClass}"`
          : "";
      const display = working;

      return `<a href="${hrefAttr}" target="_blank" rel="noopener noreferrer"${classAttr}>${display}</a>${trailing}`;
    } catch (_e) {
      return matched + trailing;
    }
  });

  return result.replace(/\n/g, "<br>");
}

/**
 * Highlight matches in HTML produced by linkifyText, without breaking tags/attributes
 * @param {string} html - HTML to highlight within
 * @param {string} term - Term to highlight
 * @returns {string} HTML with highlighted matches
 */
function highlightInHtml(html, term) {
  if (!term || !html) return html;

  const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const regex = new RegExp(escapeRegExp(term), "gi");

  const container = document.createElement("div");
  container.innerHTML = html;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT, {
    acceptNode(node) {
      return node.nodeValue && node.nodeValue.trim().length > 0
        ? NodeFilter.FILTER_ACCEPT
        : NodeFilter.FILTER_REJECT;
    },
  });

  const textNodes = [];
  while (walker.nextNode()) textNodes.push(walker.currentNode);

  textNodes.forEach((node) => {
    const text = node.nodeValue;
    if (!regex.test(text)) return;

    regex.lastIndex = 0; // reset since we reused the regex
    const frag = document.createDocumentFragment();
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(text)) !== null) {
      const before = text.slice(lastIndex, match.index);
      if (before) frag.appendChild(document.createTextNode(before));

      const mark = document.createElement("mark");
      mark.className = "bg-amber-200";
      mark.textContent = match[0];
      frag.appendChild(mark);

      lastIndex = match.index + match[0].length;
    }

    const after = text.slice(lastIndex);
    if (after) frag.appendChild(document.createTextNode(after));

    node.parentNode.replaceChild(frag, node);
  });

  return container.innerHTML;
}

/**
 * Convenience function to do both linkify and highlight in one call
 * @param {string} text - Plain text to process
 * @param {string} term - Term to highlight
 * @param {string} anchorClass - CSS classes for anchor tags
 * @returns {string} HTML with links and highlighting
 */
export function linkifyAndHighlightText(
  text,
  term,
  anchorClass = DEFAULT_ANCHOR_CLASS,
) {
  const html = linkifyText(text, anchorClass);
  if (!term) return html;
  return highlightInHtml(html, term);
}

/**
 * Extract all mention emails from text containing {{mention:email}} tokens
 * @param {string} text - Text containing mention tokens
 * @returns {string[]} Array of unique email addresses
 */
export function parseMentionEmails(text) {
  if (!text) return [];
  const mentionPattern = /\{\{mention:([^}]+)\}\}/g;
  const emails = [];
  let match;
  while ((match = mentionPattern.exec(text)) !== null) {
    emails.push(match[1]);
  }
  return [...new Set(emails)]; // Deduplicate
}

/**
 * Render mentions using a pre-populated user cache
 * @param {string} text - Text containing {{mention:email}} tokens
 * @param {Map} userCache - Map of email -> user object {name, email, id, etc}
 * @returns {string} HTML with mention chips
 */
export function renderMentions(text, userCache = new Map()) {
  if (!text) return "";

  const esc = (str) => {
    const div = document.createElement("div");
    div.textContent = str;
    return div.innerHTML;
  };

  // First escape the entire text
  let escaped = esc(text);

  // Then replace mention tokens with styled spans
  const escapedPattern = /\{\{mention:([^}]+)\}\}/g;

  const result = escaped.replace(escapedPattern, (_match, email) => {
    const user = userCache.get(email);
    const displayName = user ? user.name : email;
    // Using a span with mention styling (Slack-like)
    return `<span class="${MENTION_CHIP_CLASS}" data-email="${esc(email)}">@${esc(displayName)}</span>`;
  });

  return result;
}

/**
 * Render text with both mentions and links, optionally with highlighting
 * @param {string} text - Text containing {{mention:email}} tokens and URLs
 * @param {Map} userCache - Map of email -> user object
 * @param {string|null} highlightTerm - Optional term to highlight
 * @param {string} anchorClass - CSS classes for anchor tags
 * @returns {string} Fully decorated HTML
 */
export function renderTextWithMentions(
  text,
  userCache = new Map(),
  highlightTerm = null,
  anchorClass = DEFAULT_ANCHOR_CLASS,
) {
  if (!text) return "";

  // First render mentions (which HTML-escapes the text for security)
  // Don't convert newlines yet - renderMentions needs to escape the raw text
  let html = renderMentions(text, userCache);

  // Now convert newlines to <br> after the text has been safely escaped
  html = html.replace(/\n/g, "<br>");

  // Then linkify URLs (mentions are already HTML, so we need to be careful)
  // We'll use linkifyText on segments that aren't inside mention spans
  const container = document.createElement("div");
  container.innerHTML = html;

  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const textNodes = [];
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode);
  }

  // Check if text actually contains URLs before processing
  const URL_PATTERN = /https?:\/\/[^\s<]+|www\.[^\s<]+/i;

  textNodes.forEach((node) => {
    const text = node.textContent;
    // Only linkify if text contains actual URLs
    if (URL_PATTERN.test(text)) {
      // Text nodes from DOM won't have \n or <br> - those are separate elements
      // linkifyText's internal newline conversion is harmless here
      const linkified = linkifyText(text, anchorClass);
      const temp = document.createElement("span");
      temp.innerHTML = linkified;
      node.parentNode.replaceChild(temp, node);
    }
  });

  html = container.innerHTML;

  // Finally apply highlighting if needed
  if (highlightTerm) {
    html = highlightInHtml(html, highlightTerm);
  }

  return html;
}
